"use client";

import {
  ReactNode,
  useEffect,
  useState,
  createContext,
  useContext,
} from "react";
import { Session, User } from "@wtc/supabase";

// ============================================================================
// SIMPLE AUTH PROVIDER - FOLLOWING SUPABASE BEST PRACTICES
// ============================================================================
//
// PURPOSE: Handle ONLY Supabase session state - nothing more
// PRINCIPLES:
// - ✅ Single responsibility: Session management only
// - ✅ No database calls: Pure session state
// - ✅ No timeouts: Resolves immediately
// - ✅ No race conditions: Single initialization path
// - ✅ Fast loading: Based on localStorage session
//
// ============================================================================

interface SimpleAuthContextType {
  session: Session | null;
  user: User | null;
  isInitialized: boolean;
  isAuthenticated: boolean;
  signOut: () => Promise<void>;
}

const SimpleAuthContext = createContext<SimpleAuthContextType | undefined>(
  undefined
);

interface SimpleAuthProviderProps {
  children: ReactNode;
  supabaseClient: any; // Configurable Supabase client
}

export function SimpleAuthProvider({
  children,
  supabaseClient,
}: SimpleAuthProviderProps) {
  const [session, setSession] = useState<Session | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    let mounted = true;
    let completed = false;

    const markInitialized = () => {
      if (mounted && !completed) {
        completed = true;
        setIsInitialized(true);
      }
    };

    // Safety timeout: if getSession() hangs (slow network, cold start), allow shell to render
    const timeoutMs = 5000; // 5s for Vercel cold starts and slow networks
    const timeoutId = setTimeout(() => {
      markInitialized();
    }, timeoutMs);

    // Single initialization path - no race conditions
    const initializeAuth = async () => {
      try {
        // Get initial session (fast - from localStorage/cookies)
        const {
          data: { session },
        } = await supabaseClient.auth.getSession();

        if (mounted) {
          setSession(session);
          markInitialized();
        }
      } catch (error) {
        console.error("Error getting initial session:", error);
        if (mounted) {
          markInitialized();
        }
      } finally {
        clearTimeout(timeoutId);
      }
    };

    initializeAuth();

    // Listen for auth state changes
    const {
      data: { subscription },
    } = supabaseClient.auth.onAuthStateChange((event: string, session: Session | null) => {
      if (process.env.NODE_ENV === "development") {
        console.log("🔐 Auth state changed:", event);
      }
      if (mounted) {
        setSession(session);
        setIsInitialized(true);
      }
    });

    return () => {
      mounted = false;
      clearTimeout(timeoutId);
      subscription.unsubscribe();
    };
  }, [supabaseClient]);

  // ============================================================================
  // COMPUTED VALUES (Memoized for Performance)
  // ============================================================================

  const user = session?.user ?? null;
  const isAuthenticated = !!session?.user;

  // ============================================================================
  // AUTH ACTIONS
  // ============================================================================

  const signOut = async () => {
    try {
      // Device scope only — never global (see SignOutScope in @wtc/supabase).
      const { error } = await supabaseClient.auth.signOut({ scope: "local" });
      if (error) {
        console.error("Sign out error:", error);
        throw error;
      }
    } catch (error) {
      console.error("Sign out failed:", error);
      throw error;
    }
  };

  const contextValue: SimpleAuthContextType = {
    session,
    user,
    isInitialized,
    isAuthenticated,
    signOut,
  };

  return (
    <SimpleAuthContext.Provider value={contextValue}>
      {children}
    </SimpleAuthContext.Provider>
  );
}

// ============================================================================
// HOOK FOR CONSUMING AUTH STATE
// ============================================================================

export function useSimpleAuth(): SimpleAuthContextType {
  const context = useContext(SimpleAuthContext);
  if (context === undefined) {
    throw new Error("useSimpleAuth must be used within a SimpleAuthProvider");
  }
  return context;
}

// Non-throwing variant for boundary components (not-found / error pages) that
// may render OUTSIDE the provider tree. Returns null when no provider is mounted.
export function useSimpleAuthOptional(): SimpleAuthContextType | null {
  return useContext(SimpleAuthContext) ?? null;
}

// ============================================================================
// CONVENIENCE HOOKS
// ============================================================================

export function useSession() {
  const { session } = useSimpleAuth();
  return session;
}

export function useUser() {
  const { user } = useSimpleAuth();
  return user;
}

export function useIsAuthenticated() {
  const { isAuthenticated } = useSimpleAuth();
  return isAuthenticated;
}
